// Iter43-B — TMS section embedded inside IncidentPanel.
//
// Shipper-side primary entry point for creating transport requests within
// the context of an incident. The create modal now collects structured
// pickup/delivery addresses (postal code · city · street · gates · contact)
// + hard timing requirements (latest pickup, latest delivery, earliest
// pickup, optional delivery window) + a free-form numeric vehicle-data
// countdown (any minute value, with quick-pick chips). Per Iter43-B spec.
//
// Visibility: rendered only when the current user has the `tms` module
// access scope. The "+ Send transport request" create button is gated to
// responder+ role.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { useStore } from "../store";
import { hasModuleAccess } from "../lib/moduleAccess";
import { hasMinRole } from "./RoleGuard";
import { useTenants } from "../api/sitroom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLOR = {
  requested: "#9ec3ec",
  offers_collected: "#c89adb",
  agreed: "#f3b14a",
  dispatched: "#f3b14a",
  in_transit: "#5fa3e3",
  delivered: "#4ed084",
  cancelled: "#9aa1b3",
};

// Quick-pick chips for the free-form countdown input.
const COUNTDOWN_PRESETS = [15, 30, 45, 60, 90, 120, 180];

const mono = (size, color, weight, ls) => ({
  fontFamily: "DM Mono, monospace",
  fontSize: size,
  color: color || "#f0f2f8",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

const SectionLabel = ({ children }) => (
  <div
    className="mt-5 mb-2 uppercase"
    style={{ fontFamily: "Syne, sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "2px", color: "#9198b0" }}
  >
    {children}
  </div>
);

const EMPTY_ADDR = {
  postal_code: "",
  city: "",
  street: "",
  house_number: "",
  entry_gate: "",
  dock_gate: "",
  contact_name: "",
  contact_phone: "",
};

const formatAddr = (loc) => {
  if (!loc || typeof loc !== "object") return loc || "—";
  const street = [loc.street, loc.house_number].filter(Boolean).join(" ");
  const city = [loc.postal_code, loc.city].filter(Boolean).join(" ");
  const main = [street, city].filter(Boolean).join(", ");
  const gates = [];
  if (loc.entry_gate) gates.push(`Gate ${loc.entry_gate}`);
  if (loc.dock_gate) gates.push(`Dock ${loc.dock_gate}`);
  return main ? `${main}${gates.length ? " · " + gates.join(" · ") : ""}` : (gates.join(" · ") || "—");
};

export const TmsSection = ({ incident }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const addToast = useStore((s) => s.addToast);
  const [showCreate, setShowCreate] = useState(false);

  const enabled = !!incident?.id && hasModuleAccess(user, "tms");
  const canCreate = enabled && hasMinRole(user?.role, "responder");

  const { data: items = [], refetch } = useQuery({
    queryKey: ["incident-trs", incident?.id],
    enabled,
    queryFn: async () => {
      const r = await axios.get(`${API}/incidents/${incident.id}/transport-requests`);
      return r.data || [];
    },
  });

  if (!enabled) return null;

  return (
    <div data-testid="incident-tms-section">
      <SectionLabel>Transport Management (TMS)</SectionLabel>
      <div
        className="rounded-md p-3"
        style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.12)" }}
      >
        {items.length === 0 ? (
          <div style={mono(11, "#9198b0")} data-testid="incident-tms-empty">
            No transport requests yet for this incident.
          </div>
        ) : (
          <div className="space-y-2" data-testid="incident-tms-list">
            {items.map((tr) => (
              <TrRow
                key={tr.id}
                tr={tr}
                onOpen={() => navigate(`/tms?focus=${encodeURIComponent(tr.id)}`)}
              />
            ))}
          </div>
        )}

        {canCreate && (
          <div className="mt-3 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => navigate("/tms")}
              className="px-3 py-1.5 rounded"
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.14)",
                color: "#c5cad8",
                ...mono(10, undefined, 600, "0.4px"),
                cursor: "pointer",
              }}
              data-testid="incident-tms-open-workspace"
            >
              Open /tms workspace →
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="px-3 py-1.5 rounded"
              style={{
                background: "#185FA5",
                color: "#fff",
                ...mono(10, undefined, 700, "0.4px"),
                border: "none",
                cursor: "pointer",
              }}
              data-testid="incident-tms-create-btn"
            >
              + Send transport request
            </button>
          </div>
        )}
      </div>

      {showCreate && (
        <CreateTrModal
          incident={incident}
          onClose={() => setShowCreate(false)}
          onCreated={async () => {
            setShowCreate(false);
            await refetch();
            addToast({ type: "success", title: "Transport request sent", body: incident.id });
          }}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

const TrRow = ({ tr, onOpen }) => {
  const offered = (tr.offers || []).filter((o) => o.status === "offered").length;
  const awarded = (tr.offers || []).find((o) => o.id === tr.awarded_offer_id);
  const status = tr.status;
  return (
    <div
      onClick={onOpen}
      style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.07)",
        borderLeft: `3px solid ${STATUS_COLOR[status] || "#9aa1b3"}`,
        borderRadius: 4,
        padding: "8px 10px",
        cursor: "pointer",
      }}
      data-testid={`incident-tms-row-${tr.id}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <span style={mono(11, "#9ec3ec", 700)}>{tr.id}</span>
          <span
            style={{
              background: `${STATUS_COLOR[status] || "#9aa1b3"}28`,
              color: STATUS_COLOR[status] || "#9aa1b3",
              padding: "1px 6px",
              borderRadius: 2,
              ...mono(9, undefined, 600, "0.5px"),
              textTransform: "uppercase",
            }}
          >
            {(status || "").replace(/_/g, " ")}
          </span>
          <span style={mono(9, "#9198b0")}>{(tr.type || "").toUpperCase()}</span>
        </div>
        <span style={mono(9, "#185FA5", 600, "0.4px")}>View →</span>
      </div>
      <div className="grid grid-cols-2 gap-2" style={mono(11, "#c5cad8")}>
        <div>
          <div style={mono(8, "#9198b0", 500, "1.2px")} className="uppercase">Route</div>
          <div className="mt-0.5 truncate">{formatAddr(tr.pickup_location)} → {formatAddr(tr.delivery_location)}</div>
        </div>
        <div>
          <div style={mono(8, "#9198b0", 500, "1.2px")} className="uppercase">
            {awarded ? "Awarded carrier" : `Offers (${offered}/${(tr.offers || []).length})`}
          </div>
          <div className="mt-0.5 truncate">
            {awarded
              ? `${awarded.carrier_name} · ${(awarded.rate || 0).toLocaleString()}`
              : `${tr.carrier_tenant_ids?.length || 0} carrier(s) invited`}
          </div>
        </div>
      </div>
      {(tr.latest_pickup || tr.latest_delivery) && (
        <div className="mt-1.5 flex gap-3" style={mono(9, "#f3b14a", 600, "0.4px")}>
          {tr.latest_pickup && <span>⏱ Latest pickup: {new Date(tr.latest_pickup).toLocaleString()}</span>}
          {tr.latest_delivery && <span>⏱ Latest delivery: {new Date(tr.latest_delivery).toLocaleString()}</span>}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Create modal
// ---------------------------------------------------------------------------

export const CreateTrModal = ({ incident = null, onClose, onCreated }) => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const addToast = useStore((s) => s.addToast);
  const { data: tenants = [] } = useTenants();

  // Iter44 — when no incident is fixed, let the user link to an existing
  // open incident OR keep the TR standalone.
  const [linkMode, setLinkMode] = useState(incident ? "fixed" : "standalone");
  const [linkedIncidentId, setLinkedIncidentId] = useState(incident?.id || "");
  const [incidentSearch, setIncidentSearch] = useState("");
  const [openIncidents, setOpenIncidents] = useState([]);
  useEffect(() => {
    if (incident) return; // fixed
    let cancelled = false;
    axios.get(`${API}/incidents?status_in=open,investigating,escalated&limit=200`).then((r) => {
      if (!cancelled) setOpenIncidents(r.data || []);
    }).catch(() => {/* swallow */});
    return () => { cancelled = true; };
  }, [incident]);

  const filteredIncidents = useMemo(() => {
    const q = incidentSearch.trim().toLowerCase();
    if (!q) return openIncidents.slice(0, 25);
    return openIncidents
      .filter((i) => (i.id + " " + (i.title || "")).toLowerCase().includes(q))
      .slice(0, 25);
  }, [openIncidents, incidentSearch]);

  // Carriers = all tenants except the shipper's own tenant.
  const shipperTenantId = user?.tenant_id;
  const carriers = useMemo(
    () => (tenants || []).filter((t) => t.id !== shipperTenantId),
    [tenants, shipperTenantId],
  );

  const [type, setType] = useState("standard");
  const [selected, setSelected] = useState([]);
  const [pickupAddr, setPickupAddr] = useState({ ...EMPTY_ADDR });
  const [deliveryAddr, setDeliveryAddr] = useState({ ...EMPTY_ADDR });
  const [latestPickup, setLatestPickup] = useState("");
  const [latestDelivery, setLatestDelivery] = useState("");
  const [earliestPickup, setEarliestPickup] = useState("");
  const [winStart, setWinStart] = useState("");
  const [winEnd, setWinEnd] = useState("");
  const [cargo, setCargo] = useState("");
  const [quantity, setQuantity] = useState("");
  const [targetRate, setTargetRate] = useState("");
  const [deadlineMin, setDeadlineMin] = useState("60");
  const [tenantDefault, setTenantDefault] = useState(60);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Hydrate the field's initial value from the tenant's own default.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/tms/settings`).then((r) => {
      if (cancelled) return;
      const dflt = r.data?.default_vehicle_data_deadline_minutes;
      if (Number.isFinite(dflt)) {
        setTenantDefault(dflt);
        setDeadlineMin(String(dflt));
      }
    }).catch(() => {/* swallow — defaults are fine */});
    return () => { cancelled = true; };
  }, []);

  const toggleCarrier = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  // Map an empty-string address field to null so the backend treats it as
  // "not provided" rather than echoing back empty strings.
  const cleanAddr = (a) => {
    const out = {};
    for (const k of Object.keys(EMPTY_ADDR)) {
      const v = (a[k] || "").trim();
      if (v) out[k] = v;
    }
    return out;
  };

  const submit = async () => {
    setErr(null);
    if (selected.length === 0) {
      setErr("Select at least one carrier.");
      return;
    }
    const pu = cleanAddr(pickupAddr);
    const dl = cleanAddr(deliveryAddr);
    if (Object.keys(pu).length === 0) {
      setErr("Pickup address — fill at least one field (city, street, …).");
      return;
    }
    if (Object.keys(dl).length === 0) {
      setErr("Delivery address — fill at least one field.");
      return;
    }
    if (!latestPickup || !latestDelivery) {
      setErr("Latest pickup and latest delivery are required.");
      return;
    }
    if (new Date(latestPickup) > new Date(latestDelivery)) {
      setErr("Latest pickup must be earlier than latest delivery.");
      return;
    }
    const minutes = parseInt(deadlineMin, 10);
    if (!Number.isFinite(minutes) || minutes < 1 || minutes > 1440) {
      setErr("Vehicle-data deadline must be between 1 and 1440 minutes.");
      return;
    }
    if (!incident && linkMode === "incident" && !linkedIncidentId) {
      setErr("Pick an incident or switch to standalone.");
      return;
    }
    setBusy(true);
    try {
      const body = {
        type,
        carrier_tenant_ids: selected,
        pickup_location: pu,
        delivery_location: dl,
        latest_pickup: new Date(latestPickup).toISOString(),
        latest_delivery: new Date(latestDelivery).toISOString(),
        earliest_pickup: earliestPickup ? new Date(earliestPickup).toISOString() : null,
        delivery_window_start: winStart ? new Date(winStart).toISOString() : null,
        delivery_window_end: winEnd ? new Date(winEnd).toISOString() : null,
        cargo_description: cargo.trim() || null,
        quantity: quantity.trim() || null,
        target_rate: targetRate ? parseFloat(targetRate) : null,
        vehicle_data_deadline_minutes: minutes,
      };
      let resp;
      if (incident) {
        resp = await axios.post(`${API}/incidents/${incident.id}/transport-requests`, body);
      } else if (linkMode === "incident" && linkedIncidentId) {
        body.incident_id = linkedIncidentId;
        resp = await axios.post(`${API}/transport-requests`, body);
      } else {
        resp = await axios.post(`${API}/transport-requests`, body);
      }
      const created = resp.data;
      qc.invalidateQueries({ queryKey: ["tms"] });
      if (incident) qc.invalidateQueries({ queryKey: ["incident-trs", incident.id] });
      // Iter44 — surface auto-award path so the user sees it took effect
      if (created?.status === "agreed" && created?.contracted_rate_id) {
        addToast({
          type: "success",
          title: "Auto-awarded via contracted lane rate",
          body: `${created.id} · ${created.awarded_carrier_tenant_id} @ ${created.agreed_rate}`,
          link: { type: "tms", id: created.id },
        });
      } else if (created?.auto_award_skip_reason === "no_contracted_rate" && type === "standard") {
        addToast({
          type: "info",
          title: "No contracted rate — request sent for quotes",
          body: created.id,
          link: { type: "tms", id: created.id },
        });
      }
      onCreated(created);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      setErr(String(detail));
      addToast({ type: "error", title: "Failed to send transport request", body: String(detail) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.65)",
        zIndex: 500,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
      data-testid="incident-tms-create-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#0d0f18",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 8,
          width: "min(820px, 96vw)",
          maxHeight: "92vh",
          overflow: "auto",
          boxShadow: "0 18px 60px rgba(0,0,0,0.6)",
        }}
      >
        <div className="px-5 py-4 flex items-center justify-between"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          <div>
            <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 800, fontSize: 18, color: "#f0f2f8", letterSpacing: "-0.3px" }}>
              {incident ? "Send transport request" : "New transport request"}
            </div>
            <div className="mt-0.5" style={mono(10, "#9198b0")}>
              {incident
                ? `${incident.id} · ${incident.title || ""}`
                : "Standalone or linked to an existing incident"}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: "transparent", border: "none", color: "#9198b0", fontSize: 22, cursor: "pointer" }}
            data-testid="incident-tms-create-close"
          >×</button>
        </div>

        <div className="px-5 py-4 space-y-5">
          {/* Iter44 — Incident linkage picker (only when no incident is fixed) */}
          {!incident && (
            <div data-testid="tms-incident-link-section">
              <FieldLabel>Link to incident</FieldLabel>
              <div className="flex gap-2 mb-2">
                {[
                  { v: "standalone", label: "Standalone (no incident)" },
                  { v: "incident", label: "Link to incident" },
                ].map((m) => (
                  <button
                    type="button"
                    key={m.v}
                    onClick={() => setLinkMode(m.v)}
                    className="px-3 py-1.5 rounded"
                    style={{
                      background: linkMode === m.v ? "rgba(24,95,165,0.18)" : "transparent",
                      border: `1px solid ${linkMode === m.v ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.12)"}`,
                      color: linkMode === m.v ? "#9ec3ec" : "#c5cad8",
                      ...mono(10, undefined, 700, "0.4px"),
                      cursor: "pointer",
                    }}
                    data-testid={`tms-link-mode-${m.v}`}
                  >{m.label}</button>
                ))}
              </div>
              {linkMode === "incident" && (
                <>
                  <input
                    type="search"
                    placeholder="Search incidents by id or title…"
                    value={incidentSearch}
                    onChange={(e) => setIncidentSearch(e.target.value)}
                    className="w-full px-2 py-1.5 rounded outline-none mb-1.5"
                    style={{ background: "#08090d", border: "1px solid rgba(255,255,255,0.10)", color: "#f0f2f8", ...mono(11) }}
                    data-testid="tms-incident-search"
                  />
                  <div
                    className="rounded-md max-h-40 overflow-y-auto"
                    style={{ background: "#08090d", border: "1px solid rgba(255,255,255,0.10)" }}
                    data-testid="tms-incident-picker-list"
                  >
                    {filteredIncidents.length === 0 ? (
                      <div className="px-3 py-2" style={mono(11, "#9198b0")}>No open incidents matched.</div>
                    ) : filteredIncidents.map((i) => (
                      <label
                        key={i.id}
                        className="flex items-center gap-2 px-3 py-2 cursor-pointer"
                        style={{
                          borderBottom: "1px solid rgba(255,255,255,0.04)",
                          background: linkedIncidentId === i.id ? "rgba(24,95,165,0.10)" : "transparent",
                        }}
                      >
                        <input
                          type="radio"
                          name="tms-link-incident"
                          checked={linkedIncidentId === i.id}
                          onChange={() => setLinkedIncidentId(i.id)}
                          data-testid={`tms-pick-incident-${i.id}`}
                        />
                        <span style={mono(11, "#f0f2f8", 600)}>{i.id}</span>
                        <span style={mono(10, "#9198b0")} className="truncate">{i.title || ""}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Type toggle */}
          <div>
            <FieldLabel>Service type</FieldLabel>
            <div className="flex gap-2">
              {["standard", "sonderfahrt"].map((t) => (
                <button
                  type="button"
                  key={t}
                  onClick={() => setType(t)}
                  className="px-3 py-1.5 rounded"
                  style={{
                    background: type === t ? "rgba(24,95,165,0.18)" : "transparent",
                    border: `1px solid ${type === t ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.12)"}`,
                    color: type === t ? "#9ec3ec" : "#c5cad8",
                    ...mono(10, undefined, 700, "0.5px"),
                    textTransform: "uppercase",
                    cursor: "pointer",
                  }}
                  data-testid={`incident-tms-type-${t}`}
                >{t}</button>
              ))}
            </div>
          </div>

          {/* Carrier multi-select */}
          <div>
            <FieldLabel>Invite carriers — {selected.length} selected</FieldLabel>
            <div
              className="rounded-md max-h-40 overflow-y-auto"
              style={{ background: "#08090d", border: "1px solid rgba(255,255,255,0.10)" }}
              data-testid="incident-tms-carrier-list"
            >
              {carriers.length === 0 ? (
                <div className="px-3 py-2" style={mono(11, "#9198b0")}>No carriers available.</div>
              ) : carriers.map((c) => {
                const on = selected.includes(c.id);
                return (
                  <label
                    key={c.id}
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer"
                    style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggleCarrier(c.id)}
                      data-testid={`incident-tms-carrier-${c.id}`}
                    />
                    <span style={mono(11, "#f0f2f8", 600)}>{c.name}</span>
                    <span style={mono(9, "#9198b0")}>{c.id}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Pickup address */}
          <AddressBlock
            title="Pickup address"
            value={pickupAddr}
            onChange={setPickupAddr}
            testPrefix="incident-tms-pickup"
          />

          {/* Delivery address */}
          <AddressBlock
            title="Delivery address"
            value={deliveryAddr}
            onChange={setDeliveryAddr}
            testPrefix="incident-tms-delivery"
          />

          {/* Timing requirements */}
          <div>
            <FieldLabel>Timing requirements (hard deadlines)</FieldLabel>
            <div className="grid grid-cols-2 gap-3 mb-2">
              <DateTimeField label="Latest pickup *" value={latestPickup} onChange={setLatestPickup} testId="incident-tms-latest-pickup" />
              <DateTimeField label="Latest delivery *" value={latestDelivery} onChange={setLatestDelivery} testId="incident-tms-latest-delivery" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <DateTimeField label="Earliest pickup (optional)" value={earliestPickup} onChange={setEarliestPickup} testId="incident-tms-earliest-pickup" />
              <DateTimeField label="Delivery window start" value={winStart} onChange={setWinStart} testId="incident-tms-window-start" />
              <DateTimeField label="Delivery window end" value={winEnd} onChange={setWinEnd} testId="incident-tms-window-end" />
            </div>
          </div>

          {/* Cargo + commercial */}
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Cargo description" value={cargo} onChange={setCargo} testId="incident-tms-cargo" />
            <TextField label="Quantity" value={quantity} onChange={setQuantity} testId="incident-tms-quantity" />
          </div>
          <div className="grid grid-cols-2 gap-3 items-end">
            <TextField label="Target rate (EUR, optional)" value={targetRate} onChange={setTargetRate} type="number" testId="incident-tms-target-rate" />
            <div>
              <FieldLabel>
                Vehicle-data deadline (minutes) — tenant default: {tenantDefault}
              </FieldLabel>
              <div className="flex flex-wrap items-center gap-1.5">
                <input
                  type="number"
                  min={1}
                  max={1440}
                  value={deadlineMin}
                  onChange={(e) => setDeadlineMin(e.target.value)}
                  className="px-2 py-1.5 rounded outline-none"
                  style={{
                    background: "#08090d", border: "1px solid rgba(255,255,255,0.10)",
                    color: "#f0f2f8", width: 90, ...mono(11),
                  }}
                  data-testid="incident-tms-deadline"
                />
                {COUNTDOWN_PRESETS.map((m) => {
                  const active = String(m) === String(deadlineMin);
                  return (
                    <button
                      type="button"
                      key={m}
                      onClick={() => setDeadlineMin(String(m))}
                      className="px-2 py-1 rounded"
                      style={{
                        background: active ? "rgba(24,95,165,0.20)" : "transparent",
                        border: `1px solid ${active ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.12)"}`,
                        color: active ? "#9ec3ec" : "#c5cad8",
                        ...mono(10, undefined, 600, "0.4px"),
                        cursor: "pointer",
                      }}
                      data-testid={`incident-tms-deadline-preset-${m}`}
                    >{m}m</button>
                  );
                })}
              </div>
            </div>
          </div>

          {err && (
            <div
              className="rounded px-3 py-2"
              style={{ background: "rgba(226,75,74,0.12)", border: "1px solid rgba(226,75,74,0.4)", color: "#ff9a99", ...mono(11) }}
              data-testid="incident-tms-create-error"
            >
              {err}
            </div>
          )}
        </div>

        <div className="px-5 py-3 flex justify-end gap-2"
          style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded"
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "#c5cad8", ...mono(10, undefined, 600, "0.4px"), cursor: "pointer" }}
            data-testid="incident-tms-create-cancel"
          >Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="px-3 py-1.5 rounded"
            style={{
              background: "#185FA5",
              color: "#fff",
              ...mono(10, undefined, 700, "0.4px"),
              border: "none",
              cursor: busy ? "wait" : "pointer",
              opacity: busy ? 0.6 : 1,
            }}
            data-testid="incident-tms-create-submit"
          >{busy ? "Sending…" : "Send request"}</button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const AddressBlock = ({ title, value, onChange, testPrefix }) => {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div data-testid={`${testPrefix}-block`}>
      <FieldLabel>{title}</FieldLabel>
      <div className="grid grid-cols-12 gap-2">
        <div className="col-span-3">
          <TextField label="Postal code" value={value.postal_code} onChange={(v) => set("postal_code", v)} testId={`${testPrefix}-postal-code`} />
        </div>
        <div className="col-span-5">
          <TextField label="City" value={value.city} onChange={(v) => set("city", v)} testId={`${testPrefix}-city`} />
        </div>
        <div className="col-span-4">
          <TextField label="Street" value={value.street} onChange={(v) => set("street", v)} testId={`${testPrefix}-street`} />
        </div>
        <div className="col-span-2">
          <TextField label="House #" value={value.house_number} onChange={(v) => set("house_number", v)} testId={`${testPrefix}-house-number`} />
        </div>
        <div className="col-span-3">
          <TextField label="Entry gate" value={value.entry_gate} onChange={(v) => set("entry_gate", v)} testId={`${testPrefix}-entry-gate`} />
        </div>
        <div className="col-span-3">
          <TextField label="Dock gate" value={value.dock_gate} onChange={(v) => set("dock_gate", v)} testId={`${testPrefix}-dock-gate`} />
        </div>
        <div className="col-span-4">
          <TextField label="Contact name" value={value.contact_name} onChange={(v) => set("contact_name", v)} testId={`${testPrefix}-contact-name`} />
        </div>
        <div className="col-span-4">
          <TextField label="Contact phone" value={value.contact_phone} onChange={(v) => set("contact_phone", v)} testId={`${testPrefix}-contact-phone`} />
        </div>
      </div>
    </div>
  );
};

const FieldLabel = ({ children }) => (
  <div className="uppercase mb-1.5" style={mono(9, "#9198b0", 600, "0.8px")}>
    {children}
  </div>
);

const TextField = ({ label, value, onChange, type = "text", testId }) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <input
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-2 py-1.5 rounded outline-none"
      style={{
        background: "#08090d",
        border: "1px solid rgba(255,255,255,0.10)",
        color: "#f0f2f8",
        ...mono(11),
      }}
      data-testid={testId}
    />
  </div>
);

const DateTimeField = ({ label, value, onChange, testId }) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <input
      type="datetime-local"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-2 py-1.5 rounded outline-none"
      style={{
        background: "#08090d",
        border: "1px solid rgba(255,255,255,0.10)",
        color: "#f0f2f8",
        ...mono(11),
      }}
      data-testid={testId}
    />
  </div>
);
